import React, {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { format, formatDistanceToNow } from "date-fns";
import request from "../utils/request";
import TemplateModal from "../components/leadsComponents/TemplateModal";
import EmailComposer from "../components/leadsComponents/EmailComposer";
import EmailThread from "../components/leadsComponents/EmailThread";
import LeadStatusBadge from "../components/leadsComponents/LeadStatusBadge";

const TAB_OPTIONS = [
  { key: "members", label: "Members" },
  { key: "queue", label: "Send Queue" },
  { key: "inbox", label: "Inbox" },
  { key: "import", label: "CSV Import" },
  { key: "settings", label: "Campaign Settings" },
];

const STAGE_OPTIONS = [
  "not_contacted",
  "queued",
  "contacted",
  "replied",
  "qualified",
  "meeting_booked",
  "won",
  "lost",
  "do_not_contact",
];

const ENROLLMENT_OPTIONS = ["active", "paused", "completed", "unsubscribed", "bounced"];

const IMPORT_FIELDS = [
  ["company_name", "Company"],
  ["website", "Website"],
  ["owner_name", "Owner"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["first_name", "First Name"],
  ["last_name", "Last Name"],
  ["title", "Title"],
  ["tags", "Tags"],
  ["city", "City"],
  ["state", "State"],
];

const DEFAULT_SEQUENCE = [
  {
    stepIndex: 0,
    name: "Intro",
    subject: "Intro for {{company_name}}",
    body: "<p>Hi {{owner_name}},</p><p>Wanted to reach out about {{company_name}}.</p>",
    delayDays: 0,
    enabled: true,
  },
  {
    stepIndex: 1,
    name: "Follow Up 1",
    subject: "Quick follow up on {{company_name}}",
    body: "<p>Circling back in case this is relevant for {{company_name}}.</p>",
    delayDays: 3,
    enabled: true,
  },
  {
    stepIndex: 2,
    name: "Follow Up 2",
    subject: "Final follow up for {{company_name}}",
    body: "<p>Closing the loop for now, but happy to reconnect.</p>",
    delayDays: 7,
    enabled: true,
  },
];

function classNames(...parts) {
  return parts.filter(Boolean).join(" ");
}

function getApiErrorMessage(error, fallback) {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

function formatDateTime(value) {
  if (!value) return "—";
  return format(new Date(value), "MMM d, yyyy h:mm a");
}

function formatRelative(value) {
  if (!value) return "Never";
  return formatDistanceToNow(new Date(value), { addSuffix: true });
}

function buildThreadMessages(detail) {
  if (!detail?.lead) return [];

  const fromActivities = (detail.activities || []).map((activity) => ({
    direction: activity.direction,
    from: activity.from,
    to: activity.to,
    subject: activity.subject,
    html: activity.html,
    text: activity.text,
    timestamp: activity.occurredAt,
    threadId: activity.threadId,
    messageId: activity.providerMessageId || activity._id,
  }));

  const fromLegacy = (detail.lead.messages || []).map((message) => ({
    ...message,
    timestamp: message.timestamp,
  }));

  const seen = new Set();
  return [...fromActivities, ...fromLegacy]
    .filter((message) => {
      const key =
        message.messageId ||
        `${message.direction}-${message.threadId}-${message.subject}-${message.timestamp}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

function MetricTile({ label, value, hint }) {
  return (
    <div className="rounded-[20px] border border-white/60 bg-white/80 px-4 py-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)] backdrop-blur">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <div className="mt-3 flex items-end justify-between gap-3">
        <p className="text-2xl font-semibold text-slate-950">{value ?? 0}</p>
        {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
      </div>
    </div>
  );
}

function EmptyPanel({ title, body, action }) {
  return (
    <div className="rounded-[28px] border border-dashed border-slate-300 bg-white/70 px-8 py-10 text-center">
      <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
      <p className="mx-auto mt-3 max-w-[540px] text-sm leading-6 text-slate-600">{body}</p>
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}

function ImportSummaryTile({ label, value, hint }) {
  return (
    <div className="rounded-[22px] border border-slate-200 bg-white/85 px-4 py-4 shadow-[0_14px_32px_rgba(15,23,42,0.06)]">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-3 text-2xl font-semibold text-slate-950">{value}</p>
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

function CsvImportModal({
  isOpen,
  fileName,
  loading,
  error,
  preview,
  mapping,
  suggestedMapping,
  selectedHeaders,
  headerSearch,
  onClose,
  onOpenFilePicker,
  onHeaderSearchChange,
  onToggleHeader,
  onSelectAllHeaders,
  onClearHeaders,
  onMapField,
  onResetMapping,
  onCommit,
  committing,
}) {
  if (!isOpen) return null;

  const mappedFieldCount = Object.values(mapping || {}).filter(Boolean).length;
  const mappedFieldsByHeader = {};
  for (const [field, label] of IMPORT_FIELDS) {
    const header = mapping?.[field];
    if (!header) continue;
    mappedFieldsByHeader[header] = [...(mappedFieldsByHeader[header] || []), label];
  }

  const filteredHeaders = (preview?.headers || []).filter((header) =>
    String(header).toLowerCase().includes(String(headerSearch || "").trim().toLowerCase())
  );

  const sampleRows = (preview?.sampleRows || []).slice(0, 4);
  const selectedSampleHeaders = selectedHeaders.slice(0, 6);

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto overscroll-contain bg-slate-950/60 backdrop-blur-sm">
      <div className="min-h-full p-4 md:p-8">
        <div className="mx-auto flex min-h-[calc(100vh-2rem)] w-full max-w-[1380px] flex-col overflow-hidden rounded-[34px] border border-white/50 bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_100%)] shadow-[0_40px_100px_rgba(15,23,42,0.34)] md:min-h-[calc(100vh-4rem)]">
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#eef7f7_52%,#ffffff_100%)] px-6 py-6 md:px-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">CSV Import</p>
              <h3 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                Review columns before merge
              </h3>
              <p className="mt-2 max-w-[760px] text-sm leading-6 text-slate-600">
                Keep the columns you care about, map them into the CRM, then merge and enroll the list into this campaign.
              </p>
              <p className="mt-3 text-sm font-medium text-slate-700">
                {fileName || "No file selected"}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={onOpenFilePicker}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Choose Another File
              </button>
              <button
                onClick={onClose}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <ImportSummaryTile
              label="Rows"
              value={preview?.counts?.rows || 0}
              hint="Detected in the uploaded CSV"
            />
            <ImportSummaryTile
              label="Matched"
              value={preview?.counts?.matched || 0}
              hint="Likely updates to existing leads"
            />
            <ImportSummaryTile
              label="New"
              value={preview?.counts?.new || 0}
              hint="Likely new leads to create"
            />
            <ImportSummaryTile
              label="Mapped Fields"
              value={mappedFieldCount}
              hint={`${selectedHeaders.length} columns selected for review`}
            />
          </div>
        </div>

        <div className="grid flex-1 overflow-hidden xl:grid-cols-[0.98fr,1.12fr]">
          <div className="border-b border-slate-200 px-6 py-6 xl:border-b-0 xl:border-r md:px-8">
            <div className="flex h-full flex-col">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Detected Columns
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Use the checkboxes to keep the columns that matter. Unchecked columns drop out of the mapping flow.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={onSelectAllHeaders}
                    className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 hover:border-slate-300 hover:text-slate-700"
                  >
                    Select All
                  </button>
                  <button
                    onClick={onClearHeaders}
                    className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 hover:border-slate-300 hover:text-slate-700"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <input
                value={headerSearch}
                onChange={(event) => onHeaderSearchChange(event.target.value)}
                placeholder="Search columns"
                className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:bg-white focus:outline-none"
              />

              <div className="mt-5 flex-1 overflow-y-auto pr-1">
                {loading ? (
                  <div className="flex h-full items-center justify-center rounded-[26px] border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">
                    Preparing preview...
                  </div>
                ) : error ? (
                  <div className="rounded-[26px] border border-rose-200 bg-rose-50 px-5 py-5 text-sm text-rose-700">
                    {error}
                  </div>
                ) : preview ? (
                  <div className="space-y-3">
                    {filteredHeaders.map((header) => {
                      const isSelected = selectedHeaders.includes(header);
                      const sampleValue = preview.sampleRows?.[0]?.[header];
                      const mappedLabels = mappedFieldsByHeader[header] || [];

                      return (
                        <label
                          key={header}
                          className={classNames(
                            "flex cursor-pointer items-start gap-3 rounded-[22px] border px-4 py-4 transition",
                            isSelected
                              ? "border-cyan-300 bg-cyan-50/70 shadow-[0_12px_24px_rgba(34,211,238,0.12)]"
                              : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onToggleHeader(header)}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-500 focus:ring-cyan-400"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-slate-900">{header}</p>
                              {mappedLabels.map((label) => (
                                <span
                                  key={`${header}-${label}`}
                                  className="rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-cyan-700 ring-1 ring-cyan-200"
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                            <p className="mt-2 truncate text-sm text-slate-500">
                              {sampleValue || "No sample value in the preview rows"}
                            </p>
                          </div>
                        </label>
                      );
                    })}

                    {!filteredHeaders.length ? (
                      <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-6 text-sm text-slate-500">
                        No columns match this search.
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center rounded-[26px] border border-dashed border-slate-300 bg-slate-50 px-6 text-center text-sm text-slate-500">
                    Choose a CSV file to inspect its columns.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 py-6 md:px-8">
            <div className="flex h-full flex-col">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    CRM Mapping
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Assign selected columns to CRM fields. A column can only map to one destination.
                  </p>
                </div>

                <button
                  onClick={onResetMapping}
                  disabled={!preview}
                  className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300"
                >
                  Use Suggestions
                </button>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {IMPORT_FIELDS.map(([field, label]) => {
                  const suggestedHeader = suggestedMapping?.[field];
                  return (
                    <div
                      key={field}
                      className="rounded-[22px] border border-slate-200 bg-white px-4 py-4 shadow-[0_10px_22px_rgba(15,23,42,0.04)]"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{label}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {mapping?.[field]
                              ? `Mapped from ${mapping[field]}`
                              : suggestedHeader
                                ? `Suggested: ${suggestedHeader}`
                                : "Ignored unless you assign a column"}
                          </p>
                        </div>
                      </div>
                      <select
                        value={mapping?.[field] || ""}
                        onChange={(event) => onMapField(field, event.target.value)}
                        className="mt-3 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-cyan-400 focus:bg-white focus:outline-none"
                      >
                        <option value="">Ignore field</option>
                        {selectedHeaders.map((header) => (
                          <option key={`${field}-${header}`} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex-1 overflow-y-auto">
                <div className="rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Live Sample
                      </p>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        Previewing the first rows with the currently selected columns.
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {selectedSampleHeaders.length} shown
                    </span>
                  </div>

                  {preview && selectedHeaders.length ? (
                    <div className="mt-5 grid gap-3 lg:grid-cols-2">
                      {sampleRows.map((row, index) => (
                        <div
                          key={`sample-row-${index}`}
                          className="rounded-[22px] border border-slate-200 bg-white px-4 py-4"
                        >
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Row {index + 1}
                          </p>
                          <div className="mt-4 space-y-3">
                            {selectedSampleHeaders.map((header) => (
                              <div key={`${index}-${header}`} className="flex items-start justify-between gap-4">
                                <span className="max-w-[42%] text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                                  {header}
                                </span>
                                <span className="max-w-[58%] break-words text-right text-sm text-slate-700">
                                  {row?.[header] || "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-5 rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-5 py-6 text-sm text-slate-500">
                      Select at least one column to preview sample values.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white/90 px-6 py-5 md:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-slate-600">
              {mappedFieldCount
                ? `${mappedFieldCount} CRM fields will be imported from ${selectedHeaders.length} selected columns.`
                : "Map at least one field before merging this list."}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={onClose}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-800"
              >
                Close
              </button>
              <button
                onClick={onCommit}
                disabled={committing || !preview || !mappedFieldCount}
                className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-cyan-50 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-100"
              >
                {committing ? "Importing..." : "Merge and Enroll"}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default function LeadsDashboard() {
  const navigate = useNavigate();
  const adminInfo = useSelector((state) => state.login?.adminInfo || {});
  const gmailConnected = Boolean(adminInfo?.gmailConnected || adminInfo?.automationAllowed);
  const importInputRef = useRef(null);

  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaignId, setActiveCampaignId] = useState(null);
  const [activeTab, setActiveTab] = useState("members");
  const [overview, setOverview] = useState(null);
  const [memberRows, setMemberRows] = useState([]);
  const [queueRows, setQueueRows] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({
    q: "",
    stage: "",
    status: "",
    tags: "",
    needsAction: false,
  });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedLeadRow, setSelectedLeadRow] = useState(null);
  const [selectedLeadDetail, setSelectedLeadDetail] = useState(null);
  const [selectedQueueId, setSelectedQueueId] = useState(null);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [composeRecipient, setComposeRecipient] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [newCampaign, setNewCampaign] = useState({ name: "", description: "" });
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedImportHeaders, setSelectedImportHeaders] = useState([]);
  const [importHeaderSearch, setImportHeaderSearch] = useState("");
  const [campaignForm, setCampaignForm] = useState({
    name: "",
    description: "",
    status: "active",
    senderAlias: "",
    tracking: {
      clickTrackingEnabled: true,
      openTrackingEnabled: false,
      unsubscribeEnabled: true,
    },
    savedSegments: [],
    sequenceSteps: DEFAULT_SEQUENCE,
  });
  const [importState, setImportState] = useState({
    file: null,
    preview: null,
    mapping: {},
    loading: false,
    committing: false,
    error: "",
    suggestedMapping: {},
    result: null,
  });

  const deferredSearch = useDeferredValue(filters.q);

  const activeCampaign = useMemo(
    () => campaigns.find((campaign) => campaign._id === activeCampaignId) || null,
    [campaigns, activeCampaignId]
  );

  const selectedCampaignLead = useMemo(() => {
    if (!selectedLeadDetail?.campaigns?.length) return selectedLeadRow?.campaignLead || null;
    return (
      selectedLeadDetail.campaigns.find((entry) => entry.campaign?._id === activeCampaignId)?.campaignLead ||
      selectedLeadRow?.campaignLead ||
      null
    );
  }, [activeCampaignId, selectedLeadDetail, selectedLeadRow]);

  const threadMessages = useMemo(() => buildThreadMessages(selectedLeadDetail), [selectedLeadDetail]);

  const selectedQueuePreview =
    queueRows.find((row) => row.queueItem._id === selectedQueueId) || queueRows[0] || null;

  const mappedImportFieldCount = useMemo(
    () => Object.values(importState.mapping || {}).filter(Boolean).length,
    [importState.mapping]
  );

  useEffect(() => {
    setNoteDraft(selectedLeadDetail?.lead?.notes || "");
  }, [selectedLeadDetail]);

  useEffect(() => {
    if (!activeCampaign) return;
    setCampaignForm({
      name: activeCampaign.name || "",
      description: activeCampaign.description || "",
      status: activeCampaign.status || "active",
      senderAlias: activeCampaign.senderAlias || "",
      tracking: {
        clickTrackingEnabled: activeCampaign.tracking?.clickTrackingEnabled !== false,
        openTrackingEnabled: Boolean(activeCampaign.tracking?.openTrackingEnabled),
        unsubscribeEnabled: activeCampaign.tracking?.unsubscribeEnabled !== false,
      },
      savedSegments: activeCampaign.savedSegments || [],
      sequenceSteps: activeCampaign.sequenceSteps?.length ? activeCampaign.sequenceSteps : DEFAULT_SEQUENCE,
    });
  }, [activeCampaign]);

  useEffect(() => {
    if (!isImportModalOpen || typeof document === "undefined") return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPaddingRight = document.body.style.paddingRight;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.paddingRight = previousBodyPaddingRight;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isImportModalOpen]);

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const { data } = await request.get("/campaigns", { withCredentials: true });
      const nextCampaigns = data.campaigns || [];
      startTransition(() => {
        setCampaigns(nextCampaigns);
        setActiveCampaignId((current) => {
          if (current && nextCampaigns.some((campaign) => campaign._id === current)) return current;
          return nextCampaigns[0]?._id || null;
        });
      });
    } catch (error) {
      console.error("Failed to load campaigns", error);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  const loadLeadDetail = useCallback(async (leadId) => {
    if (!leadId) return;
    try {
      const { data } = await request.get(`/leads/${leadId}`, { withCredentials: true });
      setSelectedLeadDetail({
        lead: data.lead,
        activities: data.activities || [],
        campaigns: data.campaigns || [],
      });
    } catch (error) {
      console.error("Failed to load lead detail", error);
    }
  }, []);

  const loadWorkspace = useCallback(
    async (campaignId, nextPage = page) => {
      if (!campaignId) return;

      setLoadingWorkspace(true);
      try {
        const params = {
          page: nextPage,
          limit: 25,
          q: deferredSearch || undefined,
          stage: filters.stage || undefined,
          status: filters.status || undefined,
          tags: filters.tags || undefined,
          needsAction: filters.needsAction || undefined,
        };

        const [overviewRes, membersRes, queueRes, activityRes] = await Promise.all([
          request.get(`/campaigns/${campaignId}/overview`, { withCredentials: true }),
          request.get(`/campaigns/${campaignId}/leads`, { params, withCredentials: true }),
          request.get(`/campaigns/${campaignId}/queue`, { withCredentials: true }),
          request.get(`/campaigns/${campaignId}/activity`, { withCredentials: true }),
        ]);

        startTransition(() => {
          setOverview(overviewRes.data);
          setMemberRows(membersRes.data.leads || []);
          setPagination(membersRes.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
          setQueueRows(queueRes.data.queue || []);
          setActivityRows(activityRes.data.activities || []);
          setSelectedQueueIds((current) =>
            current.filter((id) => (queueRes.data.queue || []).some((row) => row.queueItem._id === id))
          );
          setSelectedQueueId((current) =>
            current && (queueRes.data.queue || []).some((row) => row.queueItem._id === current)
              ? current
              : queueRes.data.queue?.[0]?.queueItem?._id || null
          );
        });
      } catch (error) {
        console.error("Failed to load campaign workspace", error);
      } finally {
        setLoadingWorkspace(false);
      }
    },
    [deferredSearch, filters.needsAction, filters.stage, filters.status, filters.tags, page]
  );

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    if (!activeCampaignId) return;
    loadWorkspace(activeCampaignId, page);
  }, [activeCampaignId, page, loadWorkspace]);

  useEffect(() => {
    setPage(1);
  }, [activeCampaignId, deferredSearch, filters.stage, filters.status, filters.tags, filters.needsAction]);

  const refreshActiveCampaign = useCallback(async () => {
    if (!activeCampaignId) return;
    await Promise.all([loadCampaigns(), loadWorkspace(activeCampaignId, page)]);
    if (selectedLeadRow?.lead?._id) {
      await loadLeadDetail(selectedLeadRow.lead._id);
    }
  }, [activeCampaignId, loadCampaigns, loadLeadDetail, loadWorkspace, page, selectedLeadRow]);

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) return;

    setCreatingCampaign(true);
    try {
      const { data } = await request.post(
        "/campaigns",
        {
          name: newCampaign.name.trim(),
          description: newCampaign.description.trim(),
          status: "active",
          tracking: {
            clickTrackingEnabled: true,
            openTrackingEnabled: false,
            unsubscribeEnabled: true,
          },
          sequenceSteps: DEFAULT_SEQUENCE,
        },
        { withCredentials: true }
      );

      setNewCampaign({ name: "", description: "" });
      await loadCampaigns();
      setActiveCampaignId(data.campaign?._id || null);
    } catch (error) {
      console.error("Failed to create campaign", error);
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleSaveCampaignSettings = async () => {
    if (!activeCampaignId) return;
    try {
      await request.patch(`/campaigns/${activeCampaignId}`, campaignForm, { withCredentials: true });
      await refreshActiveCampaign();
    } catch (error) {
      console.error("Failed to save campaign settings", error);
    }
  };

  const handleSaveSegment = async () => {
    if (!activeCampaignId || !activeCampaign) return;
    const name = window.prompt("Name this segment");
    if (!name) return;

    const savedSegments = [
      ...(activeCampaign.savedSegments || []),
      { name, filters: { ...filters, q: deferredSearch } },
    ];

    try {
      await request.patch(
        `/campaigns/${activeCampaignId}`,
        { savedSegments },
        { withCredentials: true }
      );
      await refreshActiveCampaign();
    } catch (error) {
      console.error("Failed to save segment", error);
    }
  };

  const handleCampaignLeadPatch = async (campaignLeadId, patch) => {
    try {
      await request.patch(`/campaign-leads/${campaignLeadId}`, patch, { withCredentials: true });
      await refreshActiveCampaign();
    } catch (error) {
      console.error("Failed to update campaign lead", error);
    }
  };

  const handleQueueAction = async (action, ids) => {
    if (!ids.length) return;

    setLoadingWorkspace(true);
    try {
      await Promise.all(
        ids.map((id) => request.post(`/campaign-queue/${id}/${action}`, {}, { withCredentials: true }))
      );
      await refreshActiveCampaign();
    } catch (error) {
      console.error(`Failed to ${action} queue item`, error);
    } finally {
      setLoadingWorkspace(false);
    }
  };

  const handlePreviewImport = async (file) => {
    if (!file || !activeCampaignId) return;

    const formData = new FormData();
    formData.append("file", file);
    setIsImportModalOpen(true);
    setImportHeaderSearch("");

    setImportState((current) => ({
      ...current,
      file,
      loading: true,
      error: "",
      preview: null,
      mapping: {},
      suggestedMapping: {},
      result: null,
    }));

    try {
      const { data } = await request.post(`/campaigns/${activeCampaignId}/import/preview`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const lastMapping = adminInfo?.lastLeadImportMapping || {};
      const mergedMapping = { ...lastMapping, ...data.suggestedMapping };

      setImportState((current) => ({
        ...current,
        preview: data,
        mapping: mergedMapping,
        suggestedMapping: mergedMapping,
        loading: false,
      }));
      setSelectedImportHeaders(data.headers || []);
    } catch (error) {
      console.error("Failed to preview import", error);
      setImportState((current) => ({
        ...current,
        loading: false,
        error: getApiErrorMessage(error, "Preview failed"),
      }));
      setSelectedImportHeaders([]);
    }
  };

  const handleCommitImport = async () => {
    if (!activeCampaignId || !importState.preview?.sessionId) return;
    setImportState((current) => ({ ...current, committing: true, error: "" }));

    try {
      const { data } = await request.post(
        `/campaigns/${activeCampaignId}/import/commit`,
        {
          sessionId: importState.preview.sessionId,
          mapping: importState.mapping,
        },
        { withCredentials: true }
      );

      setImportState((current) => ({
        ...current,
        committing: false,
        result: data,
      }));
      setIsImportModalOpen(false);
      await refreshActiveCampaign();
    } catch (error) {
      console.error("Failed to commit import", error);
      setImportState((current) => ({
        ...current,
        committing: false,
        error: getApiErrorMessage(error, "Import failed"),
      }));
    }
  };

  const openImportPicker = () => {
    importInputRef.current?.click();
  };

  const handleImportHeaderToggle = (header) => {
    const shouldSelect = !selectedImportHeaders.includes(header);

    setSelectedImportHeaders((current) => {
      if (shouldSelect) {
        const nextSet = new Set([...current, header]);
        return (importState.preview?.headers || []).filter((item) => nextSet.has(item));
      }

      return current.filter((item) => item !== header);
    });

    if (!shouldSelect) {
      setImportState((current) => {
        const nextMapping = { ...(current.mapping || {}) };
        Object.keys(nextMapping).forEach((field) => {
          if (nextMapping[field] === header) {
            delete nextMapping[field];
          }
        });
        return {
          ...current,
          mapping: nextMapping,
        };
      });
    }
  };

  const handleSelectAllImportHeaders = () => {
    setSelectedImportHeaders(importState.preview?.headers || []);
  };

  const handleClearImportHeaders = () => {
    setSelectedImportHeaders([]);
    setImportState((current) => ({
      ...current,
      mapping: {},
    }));
  };

  const handleImportFieldMap = (field, header) => {
    setImportState((current) => {
      const nextMapping = { ...(current.mapping || {}) };

      if (!header) {
        delete nextMapping[field];
      } else {
        Object.keys(nextMapping).forEach((key) => {
          if (key !== field && nextMapping[key] === header) {
            delete nextMapping[key];
          }
        });
        nextMapping[field] = header;
      }

      return {
        ...current,
        mapping: nextMapping,
      };
    });

    if (header && !selectedImportHeaders.includes(header)) {
      const nextSet = new Set([...selectedImportHeaders, header]);
      setSelectedImportHeaders((importState.preview?.headers || []).filter((item) => nextSet.has(item)));
    }
  };

  const handleResetImportMapping = () => {
    const nextMapping = { ...(importState.suggestedMapping || {}) };
    setImportState((current) => ({
      ...current,
      mapping: nextMapping,
    }));
    const mappedHeaders = Object.values(nextMapping).filter(Boolean);
    if (mappedHeaders.length) {
      const nextSet = new Set(mappedHeaders);
      setSelectedImportHeaders((importState.preview?.headers || []).filter((item) => nextSet.has(item)));
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedLeadDetail?.lead?._id) return;
    setSavingNotes(true);
    try {
      const { data } = await request.post(
        `/leads/${selectedLeadDetail.lead._id}/notes`,
        { notes: noteDraft },
        { withCredentials: true }
      );
      setSelectedLeadDetail((current) => ({
        ...current,
        lead: data.lead,
      }));
    } catch (error) {
      console.error("Failed to save notes", error);
    } finally {
      setSavingNotes(false);
    }
  };

  const openLead = async (row) => {
    setSelectedLeadRow(row);
    setComposeRecipient("");
    await loadLeadDetail(row.lead._id);
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#eef4f6_0%,#f8fafc_32%,#ffffff_100%)]">
      {showTemplateModal ? <TemplateModal onClose={() => setShowTemplateModal(false)} /> : null}
      <CsvImportModal
        isOpen={isImportModalOpen}
        fileName={importState.file?.name}
        loading={importState.loading}
        error={importState.error}
        preview={importState.preview}
        mapping={importState.mapping}
        suggestedMapping={importState.suggestedMapping}
        selectedHeaders={selectedImportHeaders}
        headerSearch={importHeaderSearch}
        onClose={() => setIsImportModalOpen(false)}
        onOpenFilePicker={openImportPicker}
        onHeaderSearchChange={setImportHeaderSearch}
        onToggleHeader={handleImportHeaderToggle}
        onSelectAllHeaders={handleSelectAllImportHeaders}
        onClearHeaders={handleClearImportHeaders}
        onMapField={handleImportFieldMap}
        onResetMapping={handleResetImportMapping}
        onCommit={handleCommitImport}
        committing={importState.committing}
      />

      <div className="flex min-h-screen">
        <aside className="w-[320px] shrink-0 border-r border-white/60 bg-[#0f172a] px-6 py-7 text-white shadow-[24px_0_60px_rgba(15,23,42,0.18)]">
          <div className="mb-8">
            <p className="text-[11px] uppercase tracking-[0.24em] text-cyan-300/80">Campaign CRM</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight">Leads</h1>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Run outreach by campaign, review who needs attention, and send from one place.
            </p>
          </div>

          <div className="rounded-[26px] border border-white/15 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(241,245,249,0.94)_100%)] p-4 shadow-[0_18px_40px_rgba(2,6,23,0.22)]">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">New Campaign</p>
            <p className="mt-2 text-sm leading-5 text-slate-600">
              Start with the campaign name and brief. Everything else flows from this workspace.
            </p>
            <input
              value={newCampaign.name}
              onChange={(event) =>
                setNewCampaign((current) => ({ ...current, name: event.target.value }))
              }
              placeholder="Savannah PM outreach"
              className="mt-4 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 placeholder:text-slate-400 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] transition focus:border-cyan-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-cyan-200/60"
            />
            <textarea
              value={newCampaign.description}
              onChange={(event) =>
                setNewCampaign((current) => ({ ...current, description: event.target.value }))
              }
              placeholder="What this campaign is for"
              rows={3}
              className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 placeholder:text-slate-400 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] transition focus:border-cyan-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-cyan-200/60"
            />
            <button
              onClick={handleCreateCampaign}
              disabled={creatingCampaign || !newCampaign.name.trim()}
              className="mt-3 w-full rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-cyan-50 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400 disabled:text-slate-200"
            >
              {creatingCampaign ? "Creating..." : "Create Campaign"}
            </button>
          </div>

          <div className="mt-8">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Campaigns</p>
              <span className="text-xs text-slate-500">{campaigns.length}</span>
            </div>

            <div className="space-y-3">
              {loadingCampaigns ? (
                <p className="text-sm text-slate-400">Loading campaigns...</p>
              ) : campaigns.length ? (
                campaigns.map((campaign) => {
                  const isActive = campaign._id === activeCampaignId;
                  return (
                    <button
                      key={campaign._id}
                      onClick={() => setActiveCampaignId(campaign._id)}
                      className={classNames(
                        "w-full rounded-[22px] border px-4 py-4 text-left transition",
                        isActive
                          ? "border-cyan-300/60 bg-cyan-300/10 shadow-[0_16px_30px_rgba(34,211,238,0.14)]"
                          : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-white">{campaign.name}</p>
                          <p className="mt-1 text-xs leading-5 text-slate-400">
                            {campaign.description || "No campaign brief yet."}
                          </p>
                        </div>
                        <span className="rounded-full bg-slate-950/50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-300">
                          {campaign.status}
                        </span>
                      </div>

                      <div className="mt-4 flex items-center gap-4 text-xs text-slate-300">
                        <span>{campaign.stats?.enrolled || 0} enrolled</span>
                        <span>{campaign.stats?.pendingQueue || 0} pending</span>
                        <span>{campaign.stats?.replied || 0} replied</span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="text-sm text-slate-400">No campaigns yet.</p>
              )}
            </div>
          </div>
        </aside>

        <main className="relative flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto px-8 py-7">
            <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Campaign Workspace</p>
                <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                  {activeCampaign?.name || "Campaign CRM"}
                </h2>
                <p className="mt-2 max-w-[720px] text-sm leading-6 text-slate-600">
                  {activeCampaign?.description ||
                    "Segment leads by campaign, review due outreach, import lists, and send from a cleaner workflow."}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {!gmailConnected ? (
                  <button
                    onClick={() => navigate("/settings")}
                    className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800"
                  >
                    Connect Gmail
                  </button>
                ) : null}
                <button
                  onClick={() => setShowTemplateModal(true)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Edit Templates
                </button>
                <button
                  onClick={refreshActiveCampaign}
                  className="rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Refresh
                </button>
              </div>
            </div>

            {!gmailConnected ? (
              <div className="mb-6 rounded-[24px] border border-amber-200 bg-[linear-gradient(135deg,#fef3c7_0%,#fff7ed_100%)] px-5 py-4 text-sm text-amber-900">
                Gmail is required for queue sends, aliases, and threaded replies. Connect it in Settings before running live outreach.
              </div>
            ) : null}

            {!activeCampaign ? (
              <EmptyPanel
                title="Create the first outreach campaign"
                body="Campaigns are now the organizing layer for leads, queue review, imports, and conversion tracking. Create one in the left rail to start."
              />
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                  <MetricTile label="Enrolled" value={overview?.metrics?.enrolled || 0} />
                  <MetricTile label="Pending Queue" value={overview?.metrics?.pendingQueue || 0} />
                  <MetricTile label="Due Today" value={overview?.metrics?.dueToday || 0} />
                  <MetricTile label="Replied" value={overview?.metrics?.replied || 0} />
                  <MetricTile label="Qualified" value={overview?.metrics?.qualified || 0} />
                  <MetricTile
                    label="Clicks / Opens"
                    value={`${overview?.metrics?.uniqueClicks || 0} / ${overview?.metrics?.uniqueOpens || 0}`}
                  />
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {TAB_OPTIONS.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={classNames(
                        "rounded-full px-4 py-2.5 text-sm font-semibold transition",
                        activeTab === tab.key
                          ? "bg-cyan-300 text-slate-950 ring-1 ring-cyan-100 shadow-[0_12px_24px_rgba(34,211,238,0.18)]"
                          : "bg-white/92 text-slate-600 ring-1 ring-slate-200 hover:bg-white hover:text-slate-900"
                      )}
                    >
                      {tab.label}
                    </button>
                  ))}

                  {(activeCampaign.savedSegments || []).map((segment) => (
                    <button
                      key={segment._id || segment.name}
                      onClick={() =>
                        setFilters({
                          q: segment.filters?.q || "",
                          stage: segment.filters?.stage || "",
                          status: segment.filters?.status || "",
                          tags: segment.filters?.tags || "",
                          needsAction: Boolean(segment.filters?.needsAction),
                        })
                      }
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 hover:text-slate-700"
                    >
                      {segment.name}
                    </button>
                  ))}

                  <button
                    onClick={handleSaveSegment}
                    className="rounded-full border border-dashed border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 hover:border-slate-400 hover:text-slate-700"
                  >
                    Save Current Segment
                  </button>
                </div>

                <div className="mt-6">
                  {activeTab === "members" ? (
                    <section className="rounded-[28px] border border-white/60 bg-white/82 p-5 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                      <div className="grid gap-3 xl:grid-cols-[1.2fr,0.9fr,0.9fr,0.9fr,auto]">
                        <input
                          value={filters.q}
                          onChange={(event) =>
                            setFilters((current) => ({ ...current, q: event.target.value }))
                          }
                          placeholder="Search company, owner, website, or email"
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none"
                        />
                        <select
                          value={filters.stage}
                          onChange={(event) =>
                            setFilters((current) => ({ ...current, stage: event.target.value }))
                          }
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-slate-300 focus:outline-none"
                        >
                          <option value="">All stages</option>
                          {STAGE_OPTIONS.map((stage) => (
                            <option key={stage} value={stage}>
                              {stage.replace(/_/g, " ")}
                            </option>
                          ))}
                        </select>
                        <select
                          value={filters.status}
                          onChange={(event) =>
                            setFilters((current) => ({ ...current, status: event.target.value }))
                          }
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-slate-300 focus:outline-none"
                        >
                          <option value="">All enrollment</option>
                          {ENROLLMENT_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status.replace(/_/g, " ")}
                            </option>
                          ))}
                        </select>
                        <input
                          value={filters.tags}
                          onChange={(event) =>
                            setFilters((current) => ({ ...current, tags: event.target.value }))
                          }
                          placeholder="Tags, comma separated"
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none"
                        />
                        <label className="inline-flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                          <input
                            type="checkbox"
                            checked={filters.needsAction}
                            onChange={(event) =>
                              setFilters((current) => ({
                                ...current,
                                needsAction: event.target.checked,
                              }))
                            }
                            className="h-4 w-4 rounded border-slate-300"
                          />
                          Needs action
                        </label>
                      </div>

                      <div className="mt-5 overflow-hidden rounded-[22px] border border-slate-200">
                        <div className="grid grid-cols-[1.4fr,0.9fr,0.85fr,0.85fr,0.9fr,0.9fr] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          <span>Lead</span>
                          <span>Stage</span>
                          <span>Status</span>
                          <span>Next Action</span>
                          <span>Last Touch</span>
                          <span className="text-right">Owner</span>
                        </div>

                        <div className="divide-y divide-slate-200">
                          {loadingWorkspace ? (
                            <div className="px-4 py-8 text-sm text-slate-500">Loading campaign members...</div>
                          ) : memberRows.length ? (
                            memberRows.map((row) => (
                              <button
                                key={row.campaignLead._id}
                                onClick={() => openLead(row)}
                                className={classNames(
                                  "grid w-full grid-cols-[1.4fr,0.9fr,0.85fr,0.85fr,0.9fr,0.9fr] gap-3 px-4 py-4 text-left transition hover:bg-slate-50",
                                  selectedLeadRow?.campaignLead?._id === row.campaignLead._id ? "bg-cyan-50/60" : ""
                                )}
                              >
                                <div>
                                  <p className="font-semibold text-slate-900">{row.lead.company_name || "Untitled Lead"}</p>
                                  <p className="mt-1 text-sm text-slate-500">
                                    {row.lead.primary_email || row.lead.emails?.[0]?.address || "No email"}
                                  </p>
                                  <p className="mt-1 text-xs text-slate-400">{row.lead.website || "No website"}</p>
                                </div>

                                <div onClick={(event) => event.stopPropagation()}>
                                  <select
                                    value={row.campaignLead.stage}
                                    onChange={(event) =>
                                      handleCampaignLeadPatch(row.campaignLead._id, { stage: event.target.value })
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none"
                                  >
                                    {STAGE_OPTIONS.map((stage) => (
                                      <option key={stage} value={stage}>
                                        {stage.replace(/_/g, " ")}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div onClick={(event) => event.stopPropagation()}>
                                  <select
                                    value={row.campaignLead.enrollmentStatus}
                                    onChange={(event) =>
                                      handleCampaignLeadPatch(row.campaignLead._id, {
                                        enrollmentStatus: event.target.value,
                                      })
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none"
                                  >
                                    {ENROLLMENT_OPTIONS.map((status) => (
                                      <option key={status} value={status}>
                                        {status.replace(/_/g, " ")}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="text-sm text-slate-700">
                                  {formatDateTime(row.campaignLead.nextActionAt)}
                                </div>

                                <div className="text-sm text-slate-700">
                                  {formatRelative(row.lastActivity?.occurredAt || row.lead.updatedAt)}
                                </div>

                                <div className="text-right text-sm text-slate-500">
                                  {row.campaignLead.ownerName || "Unassigned"}
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-10">
                              <EmptyPanel
                                title="No campaign members match these filters"
                                body="Import a CSV, clear the filters, or enroll leads into this campaign to start tracking outreach here."
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                        <span>
                          Showing {(pagination.page - 1) * pagination.limit + 1}-
                          {Math.min(pagination.page * pagination.limit, pagination.total || 0)} of {pagination.total || 0}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            disabled={page <= 1}
                            onClick={() => setPage((current) => Math.max(1, current - 1))}
                            className="rounded-xl border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:text-slate-300"
                          >
                            Previous
                          </button>
                          <button
                            disabled={page >= pagination.pages}
                            onClick={() => setPage((current) => Math.min(pagination.pages, current + 1))}
                            className="rounded-xl border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:text-slate-300"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "queue" ? (
                    <section className="grid gap-5 xl:grid-cols-[1.4fr,0.95fr]">
                      <div className="rounded-[28px] border border-white/60 bg-white/82 p-5 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                              Pending Review Queue
                            </p>
                            <p className="mt-2 text-sm text-slate-600">
                              Review rendered outreach before anything goes out.
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleQueueAction("send", selectedQueueIds)}
                              disabled={!selectedQueueIds.length || !gmailConnected}
                              className="rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                              Send Selected
                            </button>
                            <button
                              onClick={() => handleQueueAction("skip", selectedQueueIds)}
                              disabled={!selectedQueueIds.length}
                              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300"
                            >
                              Skip Selected
                            </button>
                          </div>
                        </div>

                        <div className="overflow-hidden rounded-[22px] border border-slate-200">
                          <div className="grid grid-cols-[48px,1fr,0.8fr,0.75fr,1fr,auto] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            <span />
                            <span>Lead</span>
                            <span>Step</span>
                            <span>Due</span>
                            <span>Subject</span>
                            <span className="text-right">Actions</span>
                          </div>

                          <div className="divide-y divide-slate-200">
                            {queueRows.length ? (
                              queueRows.map((row) => {
                                const selected = selectedQueueIds.includes(row.queueItem._id);
                                return (
                                  <div
                                    key={row.queueItem._id}
                                    className={classNames(
                                      "grid grid-cols-[48px,1fr,0.8fr,0.75fr,1fr,auto] gap-3 px-4 py-4",
                                      selectedQueueId === row.queueItem._id ? "bg-cyan-50/60" : ""
                                    )}
                                  >
                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={(event) =>
                                          setSelectedQueueIds((current) =>
                                            event.target.checked
                                              ? [...current, row.queueItem._id]
                                              : current.filter((id) => id !== row.queueItem._id)
                                          )
                                        }
                                        className="h-4 w-4 rounded border-slate-300"
                                      />
                                    </div>

                                    <button
                                      onClick={() => setSelectedQueueId(row.queueItem._id)}
                                      className="text-left"
                                    >
                                      <p className="font-semibold text-slate-900">{row.lead?.company_name || "Lead"}</p>
                                      <p className="mt-1 text-sm text-slate-500">{row.queueItem.to.join(", ")}</p>
                                    </button>

                                    <div className="text-sm text-slate-700">Step {row.queueItem.stepIndex + 1}</div>
                                    <div className="text-sm text-slate-700">{formatDateTime(row.queueItem.dueAt)}</div>
                                    <div className="truncate text-sm text-slate-700">{row.queueItem.renderedSubject}</div>

                                    <div className="flex items-center justify-end gap-2">
                                      <button
                                        onClick={() => handleQueueAction("regenerate", [row.queueItem._id])}
                                        className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600"
                                      >
                                        Refresh
                                      </button>
                                      <button
                                        onClick={() => handleQueueAction("send", [row.queueItem._id])}
                                        disabled={!gmailConnected}
                                        className="rounded-xl bg-slate-950 px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                                      >
                                        Send
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-4 py-10 text-sm text-slate-500">
                                No pending queue items yet. Active enrollments will appear here when they are due.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-[28px] border border-white/60 bg-white/82 p-5 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Queue Preview
                        </p>
                        {selectedQueuePreview ? (
                          <>
                            <div className="mt-4 flex items-center justify-between">
                              <div>
                                <p className="text-lg font-semibold text-slate-950">
                                  {selectedQueuePreview.lead?.company_name}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {selectedQueuePreview.queueItem.to.join(", ")}
                                </p>
                              </div>
                              <LeadStatusBadge status={selectedQueuePreview.campaignLead?.stage || "queued"} />
                            </div>

                            <div className="mt-5 space-y-4">
                              <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Subject
                                </p>
                                <p className="mt-2 text-sm font-medium text-slate-900">
                                  {selectedQueuePreview.queueItem.renderedSubject}
                                </p>
                              </div>

                              <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Tracking
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                                    Clicks {selectedQueuePreview.queueItem.tracking?.clickTrackingEnabled ? "on" : "off"}
                                  </span>
                                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                                    Opens {selectedQueuePreview.queueItem.tracking?.openTrackingEnabled ? "on" : "off"}
                                  </span>
                                </div>
                              </div>

                              <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Email Body
                                </p>
                                <div
                                  className="prose prose-sm mt-4 max-w-none text-slate-700"
                                  dangerouslySetInnerHTML={{
                                    __html: selectedQueuePreview.queueItem.renderedHtml,
                                  }}
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <p className="mt-4 text-sm text-slate-500">
                            Select a queue item to preview the rendered subject, body, and tracking state.
                          </p>
                        )}
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "inbox" ? (
                    <section className="rounded-[28px] border border-white/60 bg-white/82 p-5 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                      <div className="mb-5 flex items-center justify-between">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Campaign Activity
                          </p>
                          <p className="mt-2 text-sm text-slate-600">
                            Replies, sends, and thread activity for this campaign.
                          </p>
                        </div>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                          {activityRows.length} recent events
                        </span>
                      </div>

                      <div className="space-y-3">
                        {activityRows.length ? (
                          activityRows.map((row) => (
                            <button
                              key={row.activity._id}
                              onClick={() => (row.lead ? openLead({ lead: row.lead, campaignLead: row.campaignLead }) : null)}
                              className="flex w-full items-start justify-between rounded-[22px] border border-slate-200 bg-white px-4 py-4 text-left transition hover:border-slate-300"
                            >
                              <div>
                                <p className="font-semibold text-slate-900">
                                  {row.lead?.company_name || row.activity.subject || "Campaign activity"}
                                </p>
                                <p className="mt-1 text-sm text-slate-500">
                                  {row.activity.direction === "inbound" ? "Inbound" : "Outbound"} ·{" "}
                                  {row.activity.subject || "No subject"}
                                </p>
                                <p className="mt-2 text-sm text-slate-600">
                                  {(row.activity.text || "").slice(0, 180) || "No preview available."}
                                </p>
                              </div>
                              <div className="ml-6 shrink-0 text-right text-xs text-slate-400">
                                <p>{formatDateTime(row.activity.occurredAt)}</p>
                                <p className="mt-1 uppercase tracking-[0.14em]">{row.activity.emailType}</p>
                              </div>
                            </button>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500">No campaign activity yet.</p>
                        )}
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "import" ? (
                    <section className="rounded-[28px] border border-white/60 bg-white/82 p-5 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                      <input
                        ref={importInputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = "";
                          handlePreviewImport(file);
                        }}
                      />

                      <div className="grid gap-6 xl:grid-cols-[1.18fr,0.82fr]">
                        <div className="rounded-[30px] border border-slate-200 bg-[linear-gradient(145deg,#ffffff_0%,#eef7f7_52%,#f8fafc_100%)] px-6 py-7 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">
                            CSV Import
                          </p>
                          <h3 className="mt-3 max-w-[620px] text-3xl font-semibold tracking-tight text-slate-950">
                            Bring a lead list in without wrestling with columns.
                          </h3>
                          <p className="mt-3 max-w-[640px] text-sm leading-6 text-slate-600">
                            Upload a CSV, review the columns in a dedicated modal, keep only what you want, and merge the list directly into this campaign.
                          </p>

                          <div className="mt-6 flex flex-wrap items-center gap-3">
                            <button
                              onClick={openImportPicker}
                              className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-cyan-50 transition hover:bg-slate-800"
                            >
                              {importState.loading ? "Preparing Preview..." : "Upload CSV"}
                            </button>
                            {importState.preview ? (
                              <button
                                onClick={() => setIsImportModalOpen(true)}
                                className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                              >
                                Review Import Plan
                              </button>
                            ) : null}
                          </div>

                          <div className="mt-6 flex flex-wrap gap-3">
                            <span className="rounded-full bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 ring-1 ring-slate-200">
                              Match by email, then website domain
                            </span>
                            <span className="rounded-full bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 ring-1 ring-slate-200">
                              Notes and manual stage stay untouched
                            </span>
                            <span className="rounded-full bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 ring-1 ring-slate-200">
                              New rows auto-enroll in this campaign
                            </span>
                          </div>

                          {importState.file ? (
                            <div className="mt-6 rounded-[22px] border border-slate-200 bg-white/80 px-4 py-4">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Current File
                              </p>
                              <p className="mt-2 text-sm font-semibold text-slate-900">{importState.file.name}</p>
                              <p className="mt-1 text-sm text-slate-500">
                                {importState.loading
                                  ? "Reading rows and preparing the import plan."
                                  : importState.preview
                                    ? "Preview ready. Open the modal to approve columns and field mappings."
                                    : "Waiting for preview results."}
                              </p>
                            </div>
                          ) : null}

                          {importState.error ? (
                            <div className="mt-4 rounded-[22px] border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
                              {importState.error}
                            </div>
                          ) : null}

                          {importState.result ? (
                            <div className="mt-4 rounded-[22px] border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-700">
                              Import complete: {importState.result.created} created, {importState.result.updated} updated, {importState.result.enrolled} enrolled.
                            </div>
                          ) : null}
                        </div>

                        <div className="space-y-4">
                          <div className="rounded-[26px] border border-slate-200 bg-white px-5 py-5 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                              Import Snapshot
                            </p>
                            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                              <ImportSummaryTile
                                label="Rows"
                                value={importState.preview?.counts?.rows || 0}
                                hint="Detected in preview"
                              />
                              <ImportSummaryTile
                                label="Mapped"
                                value={mappedImportFieldCount}
                                hint="CRM fields currently assigned"
                              />
                              <ImportSummaryTile
                                label="Matched"
                                value={importState.preview?.counts?.matched || 0}
                                hint="Likely updates"
                              />
                              <ImportSummaryTile
                                label="New"
                                value={importState.preview?.counts?.new || 0}
                                hint="Likely creations"
                              />
                            </div>
                          </div>

                          <div className="rounded-[26px] border border-slate-200 bg-white px-5 py-5 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                              Workflow
                            </p>
                            <div className="mt-4 space-y-3 text-sm text-slate-600">
                              <p>1. Upload the CSV and let the system suggest likely mappings.</p>
                              <p>2. Review the columns in the modal and uncheck anything you do not want imported.</p>
                              <p>3. Confirm the field assignments, then merge and enroll the leads into the campaign.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "settings" ? (
                    <section className="rounded-[28px] border border-white/60 bg-white/82 p-5 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                      <div className="grid gap-6 xl:grid-cols-[0.9fr,1.3fr]">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Campaign Profile
                          </p>
                          <div className="mt-4 space-y-4">
                            <label className="block">
                              <span className="text-sm font-medium text-slate-700">Name</span>
                              <input
                                value={campaignForm.name}
                                onChange={(event) =>
                                  setCampaignForm((current) => ({ ...current, name: event.target.value }))
                                }
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                              />
                            </label>

                            <label className="block">
                              <span className="text-sm font-medium text-slate-700">Description</span>
                              <textarea
                                value={campaignForm.description}
                                onChange={(event) =>
                                  setCampaignForm((current) => ({
                                    ...current,
                                    description: event.target.value,
                                  }))
                                }
                                rows={4}
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                              />
                            </label>

                            <label className="block">
                              <span className="text-sm font-medium text-slate-700">Status</span>
                              <select
                                value={campaignForm.status}
                                onChange={(event) =>
                                  setCampaignForm((current) => ({ ...current, status: event.target.value }))
                                }
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                              >
                                <option value="draft">draft</option>
                                <option value="active">active</option>
                                <option value="archived">archived</option>
                              </select>
                            </label>

                            <label className="block">
                              <span className="text-sm font-medium text-slate-700">Preferred From Alias</span>
                              <input
                                value={campaignForm.senderAlias}
                                onChange={(event) =>
                                  setCampaignForm((current) => ({
                                    ...current,
                                    senderAlias: event.target.value,
                                  }))
                                }
                                placeholder={adminInfo.gmailPrimaryAddress || adminInfo.email || "sender@domain.com"}
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                              />
                            </label>

                            <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                              <p className="text-sm font-medium text-slate-700">Tracking</p>
                              <label className="mt-4 flex items-center justify-between gap-4 text-sm text-slate-600">
                                Click tracking
                                <input
                                  type="checkbox"
                                  checked={campaignForm.tracking.clickTrackingEnabled}
                                  onChange={(event) =>
                                    setCampaignForm((current) => ({
                                      ...current,
                                      tracking: {
                                        ...current.tracking,
                                        clickTrackingEnabled: event.target.checked,
                                      },
                                    }))
                                  }
                                  className="h-4 w-4 rounded border-slate-300"
                                />
                              </label>
                              <label className="mt-3 flex items-center justify-between gap-4 text-sm text-slate-600">
                                Open tracking
                                <input
                                  type="checkbox"
                                  checked={campaignForm.tracking.openTrackingEnabled}
                                  onChange={(event) =>
                                    setCampaignForm((current) => ({
                                      ...current,
                                      tracking: {
                                        ...current.tracking,
                                        openTrackingEnabled: event.target.checked,
                                      },
                                    }))
                                  }
                                  className="h-4 w-4 rounded border-slate-300"
                                />
                              </label>
                              <label className="mt-3 flex items-center justify-between gap-4 text-sm text-slate-600">
                                One-click unsubscribe
                                <input
                                  type="checkbox"
                                  checked={campaignForm.tracking.unsubscribeEnabled}
                                  onChange={(event) =>
                                    setCampaignForm((current) => ({
                                      ...current,
                                      tracking: {
                                        ...current.tracking,
                                        unsubscribeEnabled: event.target.checked,
                                      },
                                    }))
                                  }
                                  className="h-4 w-4 rounded border-slate-300"
                                />
                              </label>
                            </div>
                          </div>
                        </div>

                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Sequence Steps
                          </p>
                          <div className="mt-4 space-y-4">
                            {(campaignForm.sequenceSteps || []).map((step, index) => (
                              <div key={step.stepIndex} className="rounded-[22px] border border-slate-200 bg-white p-4">
                                <div className="grid gap-4 md:grid-cols-[0.9fr,0.5fr,auto]">
                                  <label className="block">
                                    <span className="text-sm font-medium text-slate-700">Step Name</span>
                                    <input
                                      value={step.name}
                                      onChange={(event) =>
                                        setCampaignForm((current) => ({
                                          ...current,
                                          sequenceSteps: current.sequenceSteps.map((entry, entryIndex) =>
                                            entryIndex === index ? { ...entry, name: event.target.value } : entry
                                          ),
                                        }))
                                      }
                                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                                    />
                                  </label>

                                  <label className="block">
                                    <span className="text-sm font-medium text-slate-700">Delay Days</span>
                                    <input
                                      type="number"
                                      min="0"
                                      value={step.delayDays}
                                      onChange={(event) =>
                                        setCampaignForm((current) => ({
                                          ...current,
                                          sequenceSteps: current.sequenceSteps.map((entry, entryIndex) =>
                                            entryIndex === index
                                              ? { ...entry, delayDays: Number(event.target.value || 0) }
                                              : entry
                                          ),
                                        }))
                                      }
                                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                                    />
                                  </label>

                                  <label className="mt-7 inline-flex items-center gap-3 text-sm font-medium text-slate-700">
                                    <input
                                      type="checkbox"
                                      checked={step.enabled !== false}
                                      onChange={(event) =>
                                        setCampaignForm((current) => ({
                                          ...current,
                                          sequenceSteps: current.sequenceSteps.map((entry, entryIndex) =>
                                            entryIndex === index ? { ...entry, enabled: event.target.checked } : entry
                                          ),
                                        }))
                                      }
                                      className="h-4 w-4 rounded border-slate-300"
                                    />
                                    Enabled
                                  </label>
                                </div>

                                <label className="mt-4 block">
                                  <span className="text-sm font-medium text-slate-700">Subject</span>
                                  <input
                                    value={step.subject}
                                    onChange={(event) =>
                                      setCampaignForm((current) => ({
                                        ...current,
                                        sequenceSteps: current.sequenceSteps.map((entry, entryIndex) =>
                                          entryIndex === index ? { ...entry, subject: event.target.value } : entry
                                        ),
                                      }))
                                    }
                                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                                  />
                                </label>

                                <label className="mt-4 block">
                                  <span className="text-sm font-medium text-slate-700">Body</span>
                                  <textarea
                                    rows={5}
                                    value={step.body}
                                    onChange={(event) =>
                                      setCampaignForm((current) => ({
                                        ...current,
                                        sequenceSteps: current.sequenceSteps.map((entry, entryIndex) =>
                                          entryIndex === index ? { ...entry, body: event.target.value } : entry
                                        ),
                                      }))
                                    }
                                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                                  />
                                </label>
                              </div>
                            ))}
                          </div>

                          <div className="mt-6 flex justify-end">
                            <button
                              onClick={handleSaveCampaignSettings}
                              className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white"
                            >
                              Save Campaign
                            </button>
                          </div>
                        </div>
                      </div>
                    </section>
                  ) : null}
                </div>
              </>
            )}
          </div>

          {selectedLeadDetail?.lead ? (
            <>
              <button
                onClick={() => {
                  setSelectedLeadRow(null);
                  setSelectedLeadDetail(null);
                }}
                className="fixed inset-0 z-20 bg-slate-950/20"
                aria-label="Close lead drawer"
              />

              <aside className="fixed right-0 top-0 z-30 flex h-full w-[520px] flex-col border-l border-white/60 bg-white shadow-[0_24px_90px_rgba(15,23,42,0.18)]">
                <div className="border-b border-slate-200 px-6 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Lead Detail
                      </p>
                      <h3 className="mt-2 text-2xl font-semibold text-slate-950">
                        {selectedLeadDetail.lead.company_name || "Untitled Lead"}
                      </h3>
                      <div className="mt-3 flex items-center gap-3">
                        <LeadStatusBadge status={selectedCampaignLead?.stage || selectedLeadDetail.lead.status || "new"} />
                        <span className="text-sm text-slate-500">
                          {selectedLeadDetail.lead.primary_email ||
                            selectedLeadDetail.lead.emails?.[0]?.address ||
                            "No primary email"}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedLeadRow(null);
                        setSelectedLeadDetail(null);
                      }}
                      className="rounded-full bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-600"
                    >
                      Close
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                  <div className="grid gap-5">
                    <section className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Campaign State
                        </p>
                        <p className="text-xs text-slate-500">
                          Next action {formatDateTime(selectedCampaignLead?.nextActionAt)}
                        </p>
                      </div>

                      {selectedCampaignLead ? (
                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                          <label className="block">
                            <span className="text-sm font-medium text-slate-700">Stage</span>
                            <select
                              value={selectedCampaignLead.stage}
                              onChange={(event) =>
                                handleCampaignLeadPatch(selectedCampaignLead._id, {
                                  stage: event.target.value,
                                })
                              }
                              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none"
                            >
                              {STAGE_OPTIONS.map((stage) => (
                                <option key={stage} value={stage}>
                                  {stage.replace(/_/g, " ")}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label className="block">
                            <span className="text-sm font-medium text-slate-700">Enrollment</span>
                            <select
                              value={selectedCampaignLead.enrollmentStatus}
                              onChange={(event) =>
                                handleCampaignLeadPatch(selectedCampaignLead._id, {
                                  enrollmentStatus: event.target.value,
                                })
                              }
                              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none"
                            >
                              {ENROLLMENT_OPTIONS.map((status) => (
                                <option key={status} value={status}>
                                  {status.replace(/_/g, " ")}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">
                          This lead is not currently attached to the active campaign.
                        </p>
                      )}
                    </section>

                    <section className="rounded-[22px] border border-slate-200 bg-white p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Company
                      </p>
                      <div className="mt-4 space-y-3 text-sm text-slate-700">
                        <div>
                          <p className="font-medium text-slate-900">Website</p>
                          {selectedLeadDetail.lead.website ? (
                            <a
                              href={selectedLeadDetail.lead.website}
                              target="_blank"
                              rel="noreferrer"
                              className="text-cyan-700 hover:underline"
                            >
                              {selectedLeadDetail.lead.website}
                            </a>
                          ) : (
                            <p className="text-slate-500">No website</p>
                          )}
                        </div>

                        <div>
                          <p className="font-medium text-slate-900">Emails</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {(selectedLeadDetail.lead.emails || []).map((entry) => (
                              <button
                                key={entry.address}
                                onClick={() => setComposeRecipient(entry.address)}
                                className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200"
                              >
                                {entry.address}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <p className="font-medium text-slate-900">Contacts</p>
                          <div className="mt-2 space-y-2">
                            {(selectedLeadDetail.lead.people || []).length ? (
                              selectedLeadDetail.lead.people.map((person) => (
                                <div key={person.email || person.full_name} className="rounded-2xl border border-slate-200 px-3 py-3">
                                  <p className="font-medium text-slate-900">{person.full_name || [person.first_name, person.last_name].filter(Boolean).join(" ")}</p>
                                  <p className="text-sm text-slate-500">{person.title || "No title"}</p>
                                  {person.email ? (
                                    <button
                                      onClick={() => setComposeRecipient(person.email)}
                                      className="mt-2 text-xs font-semibold text-cyan-700 hover:underline"
                                    >
                                      {person.email}
                                    </button>
                                  ) : null}
                                </div>
                              ))
                            ) : (
                              <p className="text-slate-500">No contacts on this lead.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="rounded-[22px] border border-slate-200 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Internal Notes
                        </p>
                        <button
                          onClick={handleSaveNotes}
                          disabled={savingNotes}
                          className="rounded-xl bg-slate-950 px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                        >
                          {savingNotes ? "Saving..." : "Save"}
                        </button>
                      </div>
                      <textarea
                        rows={5}
                        value={noteDraft}
                        onChange={(event) => setNoteDraft(event.target.value)}
                        className="mt-3 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none"
                      />
                    </section>

                    <section className="rounded-[22px] border border-slate-200 bg-white p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Composer
                      </p>
                      {gmailConnected ? (
                        <div className="mt-4">
                          <EmailComposer
                            leadId={selectedLeadDetail.lead._id}
                            campaignId={activeCampaignId}
                            campaignLeadId={selectedCampaignLead?._id || null}
                            initialTo={composeRecipient}
                            threadId={selectedCampaignLead?.latestThreadId || null}
                            initialFrom={activeCampaign?.senderAlias || ""}
                            onClearTo={() => setComposeRecipient("")}
                            onSent={async () => {
                              await refreshActiveCampaign();
                              await loadLeadDetail(selectedLeadDetail.lead._id);
                            }}
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => navigate("/settings")}
                          className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800"
                        >
                          Connect Gmail to send
                        </button>
                      )}
                    </section>

                    <section className="rounded-[22px] border border-slate-200 bg-white p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Thread History
                      </p>
                      <div className="mt-4">
                        <EmailThread
                          messages={threadMessages}
                          leadId={selectedLeadDetail.lead._id}
                          campaignId={activeCampaignId}
                          campaignLeadId={selectedCampaignLead?._id || null}
                          onReply={({ from }) => setComposeRecipient(from)}
                        />
                      </div>
                    </section>
                  </div>
                </div>
              </aside>
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
}
